import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { z } from 'zod';
import { format } from 'date-fns';
import type { ListTicketsParams } from '#/components/ui/tickets/highway-maintenance-fee-penalty/endpoints/tickets';
import { HighwayMaintenanceFeePenaltyTicketState } from '#/components/ui/tickets/highway-maintenance-fee-penalty/model';

type YYYYMMDD = string;

const TicketSearchBarParams = {
  STATE: 'state',
  FROM_DATE: 'from',
  TO_DATE: 'to',
  FUZZY_SEARCH_TEXT: 'q',
} as const;

type TTicketSearchBarParams = typeof TicketSearchBarParams;

type SearchParams = {
  [TicketSearchBarParams.STATE]: ListTicketsParams['state'];
  [TicketSearchBarParams.FUZZY_SEARCH_TEXT]: string | undefined;
  [TicketSearchBarParams.FROM_DATE]: YYYYMMDD | undefined;
  [TicketSearchBarParams.TO_DATE]: YYYYMMDD | undefined;
};

type SearchParamsKeys =
  (typeof TicketSearchBarParams)[keyof typeof TicketSearchBarParams];

const stateSchema = z.array(
  z.enum(Object.values(HighwayMaintenanceFeePenaltyTicketState)),
);
const dateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .optional();

export type DefaultOptionsParams = {
  state?: SearchParams[TTicketSearchBarParams['STATE']];
  fuzzySearchText?: SearchParams[TTicketSearchBarParams['FUZZY_SEARCH_TEXT']];
  fromDate?: Date;
  toDate?: Date;
};

export function useTicketSearchBarParams(
  defaultOptions?: DefaultOptionsParams,
) {
  const [searchParams, setSearchParams] = useSearchParams();

  const rawState = searchParams.getAll(TicketSearchBarParams.STATE);
  const rawFrom = searchParams.get(TicketSearchBarParams.FROM_DATE);
  const rawTo = searchParams.get(TicketSearchBarParams.TO_DATE);

  const fuzzySearchText =
    searchParams.get(TicketSearchBarParams.FUZZY_SEARCH_TEXT) ?? undefined;
  const fromDate = dateSchema.catch('').parse(rawFrom);
  const toDate = dateSchema.catch('').parse(rawTo);
  const state = stateSchema.catch([]).parse(rawState);

  useEffect(() => {
    const defaultInit = defaultOptions
      ? (() => {
          const sp = new URLSearchParams();
          if (defaultOptions?.fuzzySearchText)
            sp.set(
              TicketSearchBarParams.FUZZY_SEARCH_TEXT,
              defaultOptions.fuzzySearchText,
            );
          if (defaultOptions?.state) {
            for (const s of defaultOptions.state) {
              sp.append(TicketSearchBarParams.STATE, s);
            }
          }
          if (defaultOptions?.fromDate)
            sp.set(
              TicketSearchBarParams.FROM_DATE,
              format(defaultOptions.fromDate, 'yyyy-MM-dd'),
            );
          if (defaultOptions?.toDate)
            sp.set(
              TicketSearchBarParams.TO_DATE,
              format(defaultOptions.toDate, 'yyyy-MM-dd'),
            );
          return sp;
        })()
      : undefined;

    if (defaultInit) {
      setSearchParams(defaultInit);
    }
    // only apply first render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setSearch = <T extends SearchParamsKeys>(
    key: T,
    value: SearchParams[T],
  ) => {
    setSearchParams((prev) => {
      if (value == null || value === '') {
        prev.delete(key);
      } else if (Array.isArray(value)) {
        prev.delete(key);
        for (const v of value) {
          prev.append(key, v);
        }
      } else {
        prev.set(key, value);
      }
      return prev;
    });
  };

  const createSearchSetter =
    <T extends SearchParamsKeys>(key: T) =>
    (value: SearchParams[T]) =>
      setSearch(key, value);

  const setState = createSearchSetter(TicketSearchBarParams.STATE);
  const setFuzzySearchText = createSearchSetter(
    TicketSearchBarParams.FUZZY_SEARCH_TEXT,
  );
  const setFromDate = createSearchSetter(TicketSearchBarParams.FROM_DATE);
  const setToDate = createSearchSetter(TicketSearchBarParams.TO_DATE);

  const setRange = (value: [YYYYMMDD, YYYYMMDD] | undefined) => {
    if (!value) {
      setSearchParams((prev) => {
        prev.delete(TicketSearchBarParams.FROM_DATE);
        prev.delete(TicketSearchBarParams.TO_DATE);
        return prev;
      });
      return;
    }
    const [from, to] = value;
    setSearchParams((prev) => {
      prev.set(TicketSearchBarParams.FROM_DATE, from);
      prev.set(TicketSearchBarParams.TO_DATE, to);
      return prev;
    });
  };

  const reset = () => {
    setSearchParams((prev) => {
      prev.delete(TicketSearchBarParams.STATE);
      prev.delete(TicketSearchBarParams.FUZZY_SEARCH_TEXT);
      prev.delete(TicketSearchBarParams.FROM_DATE);
      prev.delete(TicketSearchBarParams.TO_DATE);
      return prev;
    });
  };

  return {
    params: { state, fuzzySearchText, fromDate, toDate },
    setState,
    setFuzzySearchText,
    setFromDate,
    setToDate,
    setRange,
    setSearchParams,
    reset,
  };
}
